import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HoldsOverlay } from '../../../components/climbs/HoldsOverlay';
import { RouteLineOverlay } from '../../../components/climbs/RouteLineOverlay';
import { BoardCardActions, PendingBadge } from './BoardCardActions';
import type { RouteSettingBoardCard } from '../../../types/route-setting-board';

interface ClimbImageDialogProps {
  card: RouteSettingBoardCard | null;
  onOpenChange: (open: boolean) => void;
  /** Same two actions the card carries, so neither view is the only way in. */
  onConfirm?: (card: RouteSettingBoardCard) => void;
  onDelete?: (card: RouteSettingBoardCard) => void;
}

/**
 * The full photo with the climb drawn on it — the counterpart to the card's
 * crop, which only shows the start. Reuses the climb profile's own overlays, so
 * a boulder's circles and a route's line are the same ones a climber sees in
 * the member app.
 */
export function ClimbImageDialog({
  card,
  onOpenChange,
  onConfirm,
  onDelete,
}: ClimbImageDialogProps) {
  const { t } = useTranslation();
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  return (
    <Dialog open={card !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="min-w-0 truncate">{card?.name}</span>
            {card?.official_status === 'pending' && <PendingBadge />}
          </DialogTitle>
          <DialogDescription>
            {t(
              card?.line
                ? 'gymAdmin.routeSettingBoard.wholeLine'
                : 'gymAdmin.routeSettingBoard.allHolds'
            )}
          </DialogDescription>
        </DialogHeader>

        {card?.image_medium_url && (
          <div className="relative flex max-h-[70vh] justify-center" data-testid="climb-image-dialog">
            <img
              src={card.image_medium_url}
              alt={card.name}
              className="max-h-[70vh] w-auto object-contain"
              onLoad={(event) =>
                setNatural({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
            />
            <HoldsOverlay
              holds={card.holds}
              naturalWidth={natural?.width ?? null}
              naturalHeight={natural?.height ?? null}
              objectFit="contain"
              mode="all"
            />
            {/* Whichever of the two the climb was marked with; the other
                renders nothing. */}
            <RouteLineOverlay
              line={card.line}
              naturalWidth={natural?.width ?? null}
              naturalHeight={natural?.height ?? null}
              objectFit="contain"
            />
          </div>
        )}

        {card && (
          <DialogFooter className="sm:justify-start">
            <BoardCardActions
              card={card}
              onConfirm={onConfirm}
              onDelete={onDelete}
              variant="dialog"
            />
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
